import React from 'react';
import { buildCategoryCoverUrl, categoryNameFromItem } from '../../../config';

const ICON_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: '2',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': 'true',
};

function BriefcaseIcon() {
  return (
    <svg {...ICON_PROPS}>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M8 7V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v1" />
      <path d="M3 13h18" />
    </svg>
  );
}

function ScissorsIcon() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="6" cy="6" r="2.5" />
      <circle cx="6" cy="18" r="2.5" />
      <path d="M8.2 7.6 20 18M8.2 16.4 20 6" />
    </svg>
  );
}

function CardIcon() {
  return (
    <svg {...ICON_PROPS}>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
      <path d="M6 15h4" />
    </svg>
  );
}

function HeartIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M20.8 5.6a5.5 5.5 0 0 0-7.8 0L12 6.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21.3l7.8-7.9 1-1a5.5 5.5 0 0 0 0-7.8z" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M4 21V8l8-5 8 5v13" />
      <path d="M9 21v-6h6v6" />
      <path d="M9 10h.01M15 10h.01M12 10h.01M9 14h.01M15 14h.01" />
    </svg>
  );
}

function DocumentIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
      <path d="M8 13h8M8 17h5" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M12 3 5 6v6c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6z" />
      <path d="M9.5 12.2 11.2 14l3.4-4" />
    </svg>
  );
}

function CloudIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M18 18H7.5A4.5 4.5 0 0 1 7 9.1 6 6 0 0 1 18.6 11 3.5 3.5 0 0 1 18 18z" />
    </svg>
  );
}

function PeopleIcon() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
      <circle cx="17" cy="9" r="2.4" />
      <path d="M21.5 19a4.6 4.6 0 0 0-6.4-2.8" />
    </svg>
  );
}

export const CATEGORY_META = {
  'Layoff Triage': { theme: 'layoff', Icon: BriefcaseIcon },
  'Expense Compression': { theme: 'expense', Icon: ScissorsIcon },
  'Cash, Income & Runway': { theme: 'cash', Icon: CardIcon },
  'Healthcare & COBRA': { theme: 'healthcare', Icon: HeartIcon },
  'Company Equity': { theme: 'equity', Icon: BuildingIcon },
  'Tax Smart Moves': { theme: 'tax', Icon: DocumentIcon },
  'Insurance & Risk': { theme: 'insurance', Icon: ShieldIcon },
  'Freedom & Future': { theme: 'freedom', Icon: CloudIcon },
  'Family Milestones': { theme: 'family', Icon: PeopleIcon },
};

export const DEFAULT_CATEGORY_META = { theme: 'default', Icon: BriefcaseIcon };

function toCount(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function categoryKey(value) {
  return String(value ?? '').trim().toLowerCase();
}

export function contentsForCategory(contents, categoryName) {
  const key = categoryKey(categoryName);
  if (!key) return [];
  return (Array.isArray(contents) ? contents : []).filter(
    (item) => categoryKey(item?.category) === key,
  );
}

export function mergeCategoryCounts(apiCategories, contents) {
  const items = (Array.isArray(apiCategories) ? apiCategories : [])
    .map((item) => {
      const category = categoryNameFromItem(item);
      if (!category) return null;
      return {
        id: String(item?.id ?? '').trim(),
        category,
        cover_image: String(item?.cover_image ?? '').trim(),
        coverUrl: buildCategoryCoverUrl({
          categoryId: item?.id,
          filename: item?.cover_image,
        }),
        trackCount: toCount(item?.content_count ?? item?.contentCount),
      };
    })
    .filter(Boolean);

  const list = Array.isArray(contents) ? contents : [];
  if (list.length > 0) {
    const countByCategory = new Map();
    list.forEach((item) => {
      const key = categoryKey(item?.category);
      if (!key) return;
      countByCategory.set(key, (countByCategory.get(key) || 0) + 1);
    });
    return items
      .map((item) => {
        const count = countByCategory.get(categoryKey(item.category));
        if (!count) return null;
        return { ...item, trackCount: count };
      })
      .filter(Boolean);
  }

  return items.filter((item) => item.trackCount > 0);
}

export function formatTrackCount(count) {
  const n = Number(count) || 0;
  return `${n} ${n === 1 ? 'Quick Richuel' : 'Quick Richuels'}`;
}
